import React, { ReactNode } from "react";

import { Layout } from "react-grid-layout";
import { IntlShape } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import User from "@mapmycustomers/shared/types/User";

import GoalType from "@app/enum/dashboard/GoalType";
import DefaultTimeRangeFormComponent from "@app/scene/dashboard/components/cards/common/DefaultTimeRangeFormComponent";
import CardUiDescriptor from "@app/scene/dashboard/types/CardUiDescriptor";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import ClosedStageDealCountCardConfiguration from "@app/types/dashboard/cards/closedStageDealCount/ClosedStageDealCountCardConfiguration";
import ClosedStageDealCountViewState from "@app/types/dashboard/cards/closedStageDealCount/ClosedStageDealCountViewState";
import Scope from "@app/types/dashboard/Scope";
import { userDisplayName } from "@app/util/formatters";
import { getDealStageTypeDisplayName } from "@app/util/ui";

import { GRID_WIDTH } from "../../DashboardContent/util/consts";

import CardContentComponent from "./CardContentComponent";
import CriteriaFormComponent from "./CriteriaFormComponent";
import GoalDetailsComponent from "./GoalDetailsFormComponent";
import messages from "./messages";
import PreferencesFormComponent from "./PreferencesFormComponent";

const closedStageDealCountCard: CardUiDescriptor<ClosedStageDealCountCardConfiguration> = {
  CardContentComponent,
  CriteriaFormComponent,
  DateRangeFormComponent: DefaultTimeRangeFormComponent,
  getCardDimensions({ configuration, viewState }): Pick<Layout, "h" | "w"> {
    // TODO: should change base h depending on whether the goal is enabled/disabled
    const dimensions = {
      h: 2,
      w: (viewState as ClosedStageDealCountViewState).fullWidth ? GRID_WIDTH : 4,
    };
    if (configuration.preferences.showChart) {
      dimensions.h += 2;
    }
    return dimensions;
  },
  getCardPlainTitle(intl, context): string {
    const stageTypes = [];
    const dateRange = context.configuration.dateRange;
    const dealClosedStages = (context.extraProps as { stages: Stage[] }).stages.filter(({ id }) =>
      context.configuration.criteria.dealStageIds.includes(id)
    );

    const hasClosedWon = dealClosedStages.some(({ type }) => type === DealStageType.WON);
    if (hasClosedWon) {
      stageTypes.push(DealStageType.WON);
    }

    const hasClosedLost = dealClosedStages.some(({ type }) => type === DealStageType.LOST);
    if (hasClosedLost) {
      stageTypes.push(DealStageType.LOST);
    }

    return intl.formatMessage(messages.cardTitle, {
      date: (text: string[]) => text,
      dateRange: dateRange.range,
      dateSubRange: dateRange.subRange,
      entityList: intl.formatList(
        stageTypes.map((type) => getDealStageTypeDisplayName(intl, type)),
        { type: "conjunction" }
      ),
      stageTypes: stageTypes.length > 0,
    }) as string;
  },
  getGoalDescription(
    intl: IntlShape,
    context: { goalType: GoalType; scope: Scope },
    users: User[]
  ): ReactNode {
    const { goalType, scope } = context;
    if (scope.userId) {
      const user = users.find(({ id }) => id === scope.userId);
      return intl.formatMessage(messages.userGoal, {
        b: (text: ReactNode[]) => <b>{text}</b>,
        maxAmount: goalType === GoalType.MAX_GOAL,
        user: user ? userDisplayName(user) : intl.formatMessage(messages.user),
      });
    }
    return intl.formatMessage(scope.teamId ? messages.teamGoal : messages.orgGoal, {
      b: (text: ReactNode[]) => <b>{text}</b>,
      maxAmount: goalType === GoalType.MAX_GOAL,
    });
  },
  getGoalType(context: { configuration?: GoalConfiguration }) {
    return {
      format: "amount",
      goalType: context.configuration?.goalType ?? GoalType.MIN_GOAL,
    };
  },
  getModalTitle(intl): ReactNode {
    return intl.formatMessage(messages.modalTitle);
  },
  GoalDetailsComponent: GoalDetailsComponent,
  PreferencesFormComponent: PreferencesFormComponent,
};

export default closedStageDealCountCard;
